import { useState } from "react";
import { OWL_TIER_META, type OwlTier, type OwlHourSlip } from "../../owl/useOwlHourSlips.ts";

export type OwlWorkspaceView = "overview" | "boards" | "history" | "about";
export const OWL_WORKSPACE_TIERS: OwlTier[] = [
  "rollover",
  "moderate",
  "conservative",
  "aggressive",
  "owl_1000",
];
const MARKS: Record<OwlTier, string> = {
  rollover: "R",
  moderate: "MS",
  conservative: "C",
  aggressive: "A",
  owl_1000: "1K",
};
export function OwlWorkspaceNav({
  view,
  onChange,
}: {
  view: OwlWorkspaceView;
  onChange: (v: OwlWorkspaceView) => void;
}) {
  return (
    <nav className="owl-workspace-nav" aria-label="OWL HOUR workspace">
      {(
        [
          ["overview", "Overview"],
          ["boards", "Prediction boards"],
          ["history", "History & settlement"],
          ["about", "About OWL HOUR"],
        ] as const
      ).map(([id, label]) => (
        <button
          type="button"
          key={id}
          aria-current={view === id ? "page" : undefined}
          onClick={() => onChange(id)}
        >
          {label}
        </button>
      ))}
      <span>OWL HOUR / FOOTBALL</span>
    </nav>
  );
}
export function OwlDirectory({
  slips,
  unpricedCount,
  loading,
  error,
  onOpen,
}: {
  slips: readonly OwlHourSlip[];
  unpricedCount: number;
  loading: boolean;
  error: string | null;
  onOpen: (tier: OwlTier | "unpriced") => void;
}) {
  const [focused, setFocused] = useState<OwlTier | "unpriced">("moderate");
  const [filter, setFilter] = useState<"all" | "published">("all");
  const selected = focused === "unpriced" ? undefined : slips.find((s) => s.tier === focused);
  const count = slips.filter((s) => s.legs.length > 0).length;
  const name = focused === "unpriced" ? "Unpriced Picks" : OWL_TIER_META[focused].name;
  const rows = OWL_WORKSPACE_TIERS.filter(
    (t) => filter === "all" || slips.some((s) => s.tier === t && s.legs.length),
  );
  return (
    <section aria-label="OWL HOUR tier directory">
      <div className="owl-directory-toolbar">
        <div className="owl-filters" aria-label="Filter boards">
          <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>
            All boards <span>6</span>
          </button>
          <button
            type="button"
            aria-pressed={filter === "published"}
            onClick={() => setFilter("published")}
          >
            With selections <span>{loading ? "Loading" : count + (unpricedCount > 0 ? 1 : 0)}</span>
          </button>
        </div>
        <span className="owl-muted">Five priced tiers · one unpriced feed</span>
      </div>
      <div className="owl-discover-grid">
        <div className="owl-directory">
          <div className="owl-directory-columns">
            <span>Board / scope</span>
            <span>Selections</span>
            <span>Publication</span>
          </div>
          {rows.map((t) => {
            const slip = slips.find((s) => s.tier === t),
              meta = OWL_TIER_META[t];
            return (
              <button
                type="button"
                className={`owl-directory-row${focused === t ? " is-selected" : ""}`}
                key={t}
                aria-pressed={focused === t}
                onClick={() => setFocused(t)}
              >
                <span className="owl-board-identity">
                  <span className="owl-board-mark">{MARKS[t]}</span>
                  <span>
                    <strong>{meta.name}</strong>
                    <small>
                      {meta.legRange} · {meta.oddsRange}
                    </small>
                  </span>
                </span>
                <span>
                  {slip?.legs.length ?? 0}
                  <small>
                    {slip ? `${slip.leagues_covered.length} competitions` : "No board yet"}
                  </small>
                </span>
                <span>
                  <span className={`owl-state-pill${slip ? " is-published" : ""}`}>
                    {slip
                      ? slip.status === "settled"
                        ? "Settled"
                        : slip.meta.reduced_board
                          ? "Reduced board"
                          : "Published"
                      : loading
                        ? "Loading"
                        : error
                          ? "Unavailable"
                          : "Not published"}
                  </span>
                  <small>
                    {slip
                      ? `${slip.slip_date} · ${slip.refresh_phase === "noon" ? "12:00" : "17:00"} refresh`
                      : "Current session"}
                  </small>
                </span>
                <span className="owl-row-arrow" aria-hidden="true">
                  ↗
                </span>
              </button>
            );
          })}
          {(filter === "all" || unpricedCount > 0) && (
            <button
              type="button"
              className={`owl-directory-row${focused === "unpriced" ? " is-selected" : ""}`}
              aria-pressed={focused === "unpriced"}
              onClick={() => setFocused("unpriced")}
            >
              <span className="owl-board-identity">
                <span className="owl-board-mark">UP</span>
                <span>
                  <strong>Unpriced Picks</strong>
                  <small>Published selections without verified odds</small>
                </span>
              </span>
              <span>
                {unpricedCount}
                <small>Read only</small>
              </span>
              <span>
                <span className="owl-state-pill">Unpriced</span>
                <small>No booking access</small>
              </span>
              <span className="owl-row-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
          )}
          {rows.length === 0 && filter === "published" && unpricedCount === 0 && (
            <p className="owl-empty-inline">
              No boards with selections are available for this session.
            </p>
          )}
        </div>
        <aside className="owl-directory-aside">
          <div className="owl-preview-card">
            <span className="owl-eyebrow">EXPLORE A BOARD</span>
            <h2>{name}</h2>
            <p>
              {focused === "unpriced"
                ? "Explore the published selections. Prices still need to be verified."
                : "Open the complete selection board with exact markets, recorded odds and result states."}
            </p>
            <dl className="owl-preview-stats">
              <div>
                <dt>Selections</dt>
                <dd>{focused === "unpriced" ? unpricedCount : (selected?.legs.length ?? 0)}</dd>
              </div>
              <div>
                <dt>Combined odds</dt>
                <dd>
                  {selected?.combined_odds && selected.combined_odds > 1
                    ? selected.combined_odds.toLocaleString("en-GB", { maximumFractionDigits: 2 })
                    : "Not available"}
                </dd>
              </div>
            </dl>
            <button type="button" className="btn primary" onClick={() => onOpen(focused)}>
              Open {name} <span aria-hidden="true">↗</span>
            </button>
            <small>
              {selected
                ? `${selected.slip_date} · ${selected.refresh_phase === "noon" ? "Day" : "Evening"} refresh`
                : focused === "unpriced"
                  ? "Read-only predictions"
                  : "Availability is shown honestly for every board."}
            </small>
          </div>
          <div className="owl-reading-note">
            <strong>Every selection, in order.</strong>
            <p>
              Review the fixture and exact market first. Expand a leg for its supporting notes and
              recorded context.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
